import React from 'react';
import { motion } from 'framer-motion';
import { FaBriefcase, FaCheckCircle } from 'react-icons/fa';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { sectionStyles } from '../styles/common';
import { scrollAnimation, staggerChildren } from '../styles/animations';

/** 최신 경력이 위에 오도록 `careers.<id>` 순서를 맞춥니다. */
const CAREER_IDS = ['thebytesize', 'foxee'] as const;

function getCareerDetails(t: TFunction, careerId: (typeof CAREER_IDS)[number]): string[] {
  const raw = t(`careers.${careerId}.details`, { returnObjects: true });
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return [];
  }
  const entries = Object.entries(raw as Record<string, string>);
  return entries
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, value]) => value);
}

const Careers: React.FC = () => {
  const { t } = useTranslation();

  const careers = CAREER_IDS.map((id) => ({
    id,
    company: t(`careers.${id}.company`),
    position: t(`careers.${id}.position`),
    period: t(`careers.${id}.period`),
    description: t(`careers.${id}.description`),
    details: getCareerDetails(t, id)
  }));

  return (
    <section id="careers" className={sectionStyles.wrapper}>
      <div className={sectionStyles.container}>
        <motion.h2 
          className={sectionStyles.title}
          {...scrollAnimation}
        >
          {t('careers.title')}
        </motion.h2>
        
        <motion.div 
          className={sectionStyles.content}
          variants={staggerChildren.container}
          initial="initial"
          animate="animate"
        >
          <div className="space-y-6">
            {careers.map((career) => (
              <motion.div
                key={career.id}
                variants={staggerChildren.item}
                className={sectionStyles.card}
              >
                <div className="flex items-start">
                  <span className="text-2xl text-indigo-600 dark:text-indigo-400 mr-4">
                    <FaBriefcase />
                  </span>
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {career.company}
                      </h3>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {career.period}
                      </span>
                    </div>
                    <p className="text-lg font-medium text-indigo-600 dark:text-indigo-400 mb-3">
                      {career.position}
                    </p>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      {career.description}
                    </p>
                    <ul className="space-y-2">
                      {career.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-start">
                          <span className="text-indigo-600 dark:text-indigo-400 mr-2 flex-shrink-0 mt-0.5">
                            <FaCheckCircle />
                          </span>
                          <span className="text-gray-600 dark:text-gray-300 text-sm">
                            {detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Careers;
